import { useState } from 'react';

import { Field, TextInput } from '../../components/index.ts';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from './datetime.ts';

type OccurredAtFieldProps = {
  value: Date;
  onChange: (value: Date) => void;
  label?: string;
  error?: string | undefined;
};

/**
 * Date and time, pre-filled with now and freely editable in either direction.
 * There is no backdating limit by design — a trainee catching up on a month of
 * nights is a supported workflow, not an error.
 *
 * The text is held locally so a half-typed date does not blank the stored
 * value; only a complete, real moment is propagated upwards.
 */
export function OccurredAtField({
  value,
  onChange,
  label = 'Date and time',
  error,
}: OccurredAtFieldProps) {
  const [text, setText] = useState(() => toDateTimeLocalValue(value));
  const [invalid, setInvalid] = useState(false);

  /*
   * Follows the value when it changes underneath us — which happens to a
   * procedure inside a case when the case's own date moves. Without this the
   * input would keep showing the old time while the record held the new one.
   */
  const expected = toDateTimeLocalValue(value);
  const [lastSeen, setLastSeen] = useState(expected);
  if (expected !== lastSeen) {
    setLastSeen(expected);
    setText(expected);
    setInvalid(false);
  }

  return (
    <Field label={label} error={error ?? (invalid ? 'Enter a real date and time.' : undefined)}>
      <TextInput
        type="datetime-local"
        value={text}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);

          const parsed = fromDateTimeLocalValue(next);
          setInvalid(parsed === null);
          if (parsed !== null) onChange(parsed);
        }}
      />
    </Field>
  );
}
